import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  AlertTriangle, 
  RotateCcw, 
  X, 
  ShieldAlert, 
  CheckCircle2, 
  Database, 
  Lock, 
  Sliders, 
  ShieldCheck 
} from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';

interface ResetEntireSettingsConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmReset: () => void;
}

export const ResetEntireSettingsConfirmationModal: React.FC<ResetEntireSettingsConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirmReset,
}) => {
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset acknowledgment checkbox when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setHasAcknowledged(false);
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Handle ESC key press to dismiss safely
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isProcessing) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, isProcessing]);

  if (typeof document === 'undefined') return null;

  const handleConfirm = () => {
    if (!hasAcknowledged || isProcessing) return;
    triggerHaptic('heavy');
    setIsProcessing(true);

    // Execute the reset and close
    setTimeout(() => {
      onConfirmReset();
      setIsProcessing(false);
      onClose();
    }, 350);
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div 
          id="reset-settings-portal-container"
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-indigo-950/80 backdrop-blur-md"
        >
          {/* Backdrop Click Dismiss */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0"
            onClick={() => {
              if (!isProcessing) onClose();
            }}
          />

          {/* Dialog Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            id="reset-settings-confirmation-dialog"
            className="relative w-full max-w-lg bg-gradient-to-b from-indigo-950 via-indigo-900 to-indigo-950 border border-rose-500/40 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl shadow-rose-950/50 text-white z-10 space-y-6 overflow-hidden"
          >
            {/* Ambient Danger Glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-rose-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />

            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3.5">
                <div className="w-12 h-12 rounded-2xl bg-rose-500/20 border border-rose-500/40 text-rose-400 flex items-center justify-center shrink-0 shadow-lg shadow-rose-500/10">
                  <ShieldAlert className="w-6 h-6 animate-pulse" />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider font-mono text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded-full border border-rose-500/20">
                    High Impact Action
                  </span>
                  <h3 className="text-xl font-black italic uppercase tracking-tight text-white mt-1">
                    Confirm Settings Reset
                  </h3>
                </div>
              </div>

              <button
                type="button"
                id="btn-close-reset-confirm-dialog"
                onClick={onClose}
                disabled={isProcessing}
                className="p-2 text-indigo-300 hover:text-white rounded-xl hover:bg-white/10 transition disabled:opacity-30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Description & Impact Summary */}
            <div className="space-y-4">
              <p className="text-xs text-indigo-200/90 leading-relaxed font-medium">
                You are about to revert <strong className="text-white">all application-wide settings and authentication configurations</strong> to their default factory states in Firestore.
              </p>

              {/* What will be reset list */}
              <div className="bg-indigo-950/90 rounded-2xl p-4 border border-white/10 space-y-2.5 text-xs">
                <div className="font-bold text-amber-300 flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
                  <span>The following Firestore configurations will be restored:</span>
                </div>
                <ul className="space-y-1.5 text-indigo-200/80 font-medium pl-1 text-[11px]">
                  <li className="flex items-center space-x-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                    <span><strong>OAuth Providers:</strong> Google, Apple, and Facebook sign-ins enabled</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                    <span><strong>Security Whitelists:</strong> Allowed email domains and blocked IP entries cleared</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                    <span><strong>Rate Limiting & Thresholds:</strong> Strict rate limiting disabled; 5-attempt suspension threshold restored</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                    <span><strong>UI Preferences:</strong> Default toggles for badges, sync status, and mini-games synchronized</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Secondary Confirmation Checkbox */}
            <label 
              id="lbl-confirm-acknowledge-checkbox"
              className="flex items-start space-x-3 p-3.5 rounded-2xl bg-rose-950/40 border border-rose-500/30 hover:border-rose-500/60 transition cursor-pointer select-none"
            >
              <input
                type="checkbox"
                id="chk-acknowledge-reset-settings"
                checked={hasAcknowledged}
                onChange={(e) => {
                  triggerHaptic('light');
                  setHasAcknowledged(e.target.checked);
                }}
                disabled={isProcessing}
                className="mt-0.5 w-4 h-4 rounded border-rose-400 text-rose-600 focus:ring-rose-500 focus:ring-offset-indigo-950"
              />
              <span className="text-xs text-rose-200 font-semibold leading-snug">
                I understand that this action will overwrite all live authentication toggles, whitelists, and security thresholds in Firestore.
              </span>
            </label>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-2">
              <button
                type="button"
                id="btn-cancel-reset-settings"
                onClick={onClose}
                disabled={isProcessing}
                className="w-full sm:w-auto px-5 py-3 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 hover:text-white border border-white/10 rounded-2xl text-xs font-black italic uppercase transition disabled:opacity-40"
              >
                Cancel & Keep Settings
              </button>

              <button
                type="button"
                id="btn-confirm-reset-entire-settings"
                onClick={handleConfirm}
                disabled={!hasAcknowledged || isProcessing}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-rose-600 to-amber-500 hover:from-rose-500 hover:to-amber-400 disabled:opacity-40 disabled:pointer-events-none text-white font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-rose-600/30 border border-white/20 transition flex items-center justify-center space-x-2 cursor-pointer"
              >
                <RotateCcw className={`w-4 h-4 stroke-[2.5] ${isProcessing ? 'animate-spin' : ''}`} />
                <span>{isProcessing ? 'Resetting Firestore...' : 'Yes, Reset Entire Settings'}</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
